<template>
  <div class="step-page">
    <div class="step-header">
      <div class="step-badge">Projet : {{ projectName }}</div>
      <h1 class="step-title">Profil de consommation d'électricité</h1>
    </div>

    <div class="step-content two-col">
      <!-- Colonne gauche : informations du profil -->
      <div class="card-block" @input="markDirty" @change="markDirty">
        <div class="card-block__title">Informations du profil de consommation</div>

        <div class="form-group">
          <label class="form-label">Type de tarif</label>
          <div class="tarif-type-options">
            <label class="tarif-type-option" :class="{ 'tarif-type-option--active': form.typeTarif === 'unique' }">
              <input type="radio" v-model="form.typeTarif" value="unique" @change="calculateEstimation" />
              Tarif unique
            </label>
            <label class="tarif-type-option" :class="{ 'tarif-type-option--active': form.typeTarif === 'hchp' }">
              <input type="radio" v-model="form.typeTarif" value="hchp" @change="calculateEstimation" />
              HP / HC — Toute l'année
            </label>
            <label class="tarif-type-option" :class="{ 'tarif-type-option--active': form.typeTarif === 'saisonnier' }">
              <input type="radio" v-model="form.typeTarif" value="saisonnier" @change="calculateEstimation" />
              HP / HC — Hiver / Été
            </label>
            <label class="tarif-type-option" :class="{ 'tarif-type-option--active': form.typeTarif === 'hsc' }">
              <input type="radio" v-model="form.typeTarif" value="hsc" @change="calculateEstimation" />
              HP / HC / HSC — Toute l'année
            </label>
            <label class="tarif-type-option" :class="{ 'tarif-type-option--active': form.typeTarif === 'hsc_saisonnier' }">
              <input type="radio" v-model="form.typeTarif" value="hsc_saisonnier" @change="calculateEstimation" />
              HP / HC / HSC — Hiver / Été
            </label>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Puissance d'abonnement (kVA)</label>
          <input v-model.number="form.puissanceAbonnement" type="number" min="1" step="1" class="form-input" placeholder="Ex : 6" />
        </div>

        <div class="form-group">
          <label class="form-label">Raccordement</label>
          <div class="tarif-type-selector">
            <!-- professionnel : toujours triphasé (29/10/2026) -->
            <label v-if="!estPro" class="tarif-type-option" :class="{ 'tarif-type-option--active': form.raccordement === 'mono' }">
              <input type="radio" v-model="form.raccordement" value="mono" /> Monophasé
            </label>
            <label class="tarif-type-option" :class="{ 'tarif-type-option--active': form.raccordement === 'tri' }">
              <input type="radio" v-model="form.raccordement" value="tri" /> Triphasé
            </label>
          </div>
        </div>

        <!-- Tarif d'électricité -->
        <div class="section-separator">Tarif d'électricité</div>
        <div class="info-hint">Vous pouvez retrouver ces informations sur votre contrat d'énergie.</div>

        <div class="form-group">
          <label class="form-label">Tarif de l'abonnement (€ HT/an)</label>
          <input v-model.number="form.tarifAbonnementAnnuel" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 188.88" />
        </div>

        <!-- Tarif unique -->
        <div v-if="form.typeTarif === 'unique'" class="form-group">
          <label class="form-label">Tarif (c€/kWh HT)</label>
          <input v-model.number="form.tarifHP" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 20.81" />
        </div>

        <!-- HC/HP toute l'année (et HP/HC/HSC) -->
        <div v-if="form.typeTarif === 'hchp' || form.typeTarif === 'hsc'" class="form-row">
          <div class="form-group form-group--half">
            <label class="form-label">Tarif HP (c€/kWh HT)</label>
            <input v-model.number="form.tarifHP" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 20.81" />
          </div>
          <div class="form-group form-group--half">
            <label class="form-label">Tarif HC (c€/kWh HT)</label>
            <input v-model.number="form.tarifHC" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 16.35" />
          </div>
        </div>
        <div v-if="form.typeTarif === 'hsc'" class="form-group">
          <label class="form-label">Tarif HSC — heures super creuses (c€/kWh HT)</label>
          <input v-model.number="form.tarifHSC" type="number" min="0" step="0.01" class="form-input tarif-hsc" placeholder="Ex : 13.35" />
        </div>

        <!-- HC/HP saisonnier (et HP/HC/HSC été / hiver) -->
        <div v-if="form.typeTarif === 'saisonnier' || form.typeTarif === 'hsc_saisonnier'">
          <div class="tarif-saison-group">
            <div class="tarif-saison-label">Hiver</div>
            <div class="form-row">
              <div class="form-group form-group--half">
                <label class="form-label">HP Hiver (c€/kWh HT)</label>
                <input v-model.number="form.tarifHPHiver" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 20.81" />
              </div>
              <div class="form-group form-group--half">
                <label class="form-label">HC Hiver (c€/kWh HT)</label>
                <input v-model.number="form.tarifHCHiver" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 16.35" />
              </div>
            </div>
            <div v-if="form.typeTarif === 'hsc_saisonnier'" class="form-group">
              <label class="form-label">HSC Hiver (c€/kWh HT)</label>
              <input v-model.number="form.tarifHSCHiver" type="number" min="0" step="0.01" class="form-input tarif-hsc" placeholder="Ex : 19.92" />
            </div>
          </div>
          <div class="tarif-saison-group">
            <div class="tarif-saison-label">Été</div>
            <div class="form-row">
              <div class="form-group form-group--half">
                <label class="form-label">HP Été (c€/kWh HT)</label>
                <input v-model.number="form.tarifHPEte" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 20.81" />
              </div>
              <div class="form-group form-group--half">
                <label class="form-label">HC Été (c€/kWh HT)</label>
                <input v-model.number="form.tarifHCEte" type="number" min="0" step="0.01" class="form-input" placeholder="Ex : 16.35" />
              </div>
            </div>
            <div v-if="form.typeTarif === 'hsc_saisonnier'" class="form-group">
              <label class="form-label">HSC Été (c€/kWh HT)</label>
              <input v-model.number="form.tarifHSCEte" type="number" min="0" step="0.01" class="form-input tarif-hsc" placeholder="Ex : 9.91" />
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Taxe Acheminement et Accise (c€/kWh HT)</label>
          <input v-model.number="form.taxeelec" type="number" min="0" step="0.001" class="form-input" placeholder="Ex : 3.09" />
          <div class="form-hint">TURPE + accise sur l'électricité</div>
        </div>

        <!-- prix saisi en €/kWh au lieu de c€/kWh (PALLUEL, 14/10/2026 : 0,21 « c€ » → facture de 123 € pour 50 MWh) -->
        <div v-if="tarifsEnEuros.length" class="alerte-tarifs">
          <p class="alerte-tarifs__msg">
            Les prix se saisissent en <b>centimes d'euro</b> par kWh.
            {{ tarifsEnEuros.length > 1 ? 'Ces prix sont invraisemblables' : 'Ce prix est invraisemblable' }} :
            <span v-for="(t, i) in tarifsEnEuros" :key="t.cle">{{ i ? ', ' : '' }}{{ t.libelle }} {{ nombreFr(t.valeur) }} c€/kWh<span v-if="i === tarifsEnEuros.length - 1">.</span></span>
            Vouliez-vous dire
            <span v-for="(t, i) in tarifsEnEuros" :key="t.cle + '-x100'">{{ i ? ', ' : '' }}{{ nombreFr(t.valeur * 100) }}</span> c€/kWh ?
          </p>
          <button type="button" class="btn-unit-validate" @click="corrigerTarifsEnEuros">Corriger (× 100)</button>
        </div>

        <!-- Plages horaires : HC (et HSC, toute l'année ou par saison — heures super creuses) -->
        <template v-for="liste in listesPlages">
          <div :key="liste.cle + '-titre'" class="section-separator">{{ liste.titre }}</div>
          <div :key="liste.cle" :class="'plages plages--' + liste.cle">
            <div v-for="(periode, idx) in form[liste.cle]" :key="idx" class="form-row periode-row">
              <div class="form-group form-group--half">
                <label class="form-label">Début {{ liste.court }} {{ idx + 1 }}</label>
                <input v-model="periode.debut" type="time" class="form-input" />
              </div>
              <div class="form-group form-group--half">
                <label class="form-label">Fin {{ liste.court }} {{ idx + 1 }}</label>
                <input v-model="periode.fin" type="time" class="form-input" />
              </div>
              <button v-if="form[liste.cle].length > liste.min" class="btn-remove-period" @click="removePeriode(idx, liste.cle)">✕</button>
            </div>
            <button class="btn-add-period" @click="addPeriode(liste.cle)">+ Ajouter une tranche {{ liste.court }}</button>
          </div>
        </template>

        <div v-if="isDirty && curveStats" class="recalcul-row">
          <button class="btn-recalcul-form" :disabled="statsLoading" @click="recomputeStats">
            {{ statsLoading ? 'Recalcul...' : 'Recalculer le bilan' }}
          </button>
        </div>

      </div>

      <!-- Colonne droite : source de données -->
      <div class="card-block">
        <div class="card-block__title">Source du profil de consommation</div>
        <!-- mode opératoire à transmettre au client, en PDF (19/10/2026) : professionnel = espace Enedis Entreprise, collecte
             et historique de la courbe (13/10/2026) ; particulier = accord Data Connect depuis le lien de consentement -->
        <a v-if="!pdlDisponible" class="mode-operatoire" href="/mode-operatoire-enedis-entreprise.pdf" download>
          &#8681; Télécharger le mode opératoire pour créer l'espace Enedis Entreprise et récupérer la courbe de charge
        </a>
        <a v-else class="mode-operatoire" href="/mode-operatoire-enedis-particulier.pdf" download>
          &#8681; Télécharger le mode opératoire pour le client (donner son accord à Enedis)
        </a>

        <!-- Option 1 : Via PDL Enedis — particuliers seulement (13/10/2026 : Data Connect n'existe pas sur l'espace
             Entreprise d'Enedis ; les courbes des professionnels passeront par SGE Tiers) -->
        <div class="radio-option option-pdl" :class="{ 'radio-option--active': form.profileSource === 'pdl', 'radio-option--disabled': !pdlDisponible }"
          :title="pdlDisponible ? null : 'Non disponible pour un client professionnel'">
          <label class="radio-label">
            <input type="radio" value="pdl" v-model="form.profileSource" :disabled="!pdlDisponible" />
            Consommation via PDL (Enedis)
          </label>
          <p v-if="pdlDisponible" class="radio-hint">Choisissez le consentement Enedis du client : sa courbe de charge des 365 derniers jours est chargée aussitôt.</p>
          <p v-else class="radio-hint">Non disponible pour un client professionnel : Enedis ne propose pas le consentement en ligne pour ses compteurs. Importez le fichier de sa courbe de charge.</p>
          <div v-if="form.profileSource === 'pdl' && pdlDisponible" class="radio-content">
            <!-- Consentements reçus (règle du 12/10/2026) : liste vide par défaut, chargement dès la sélection -->
            <div class="form-group">
              <label class="form-label" for="consentement-enedis">Consentement du client</label>
              <!-- liste déroulante avec recherche intégrée (nom, e-mail, PRM ; 12/10/2026), vide par défaut -->
              <div class="consent-choix">
                <div class="consent-combo">
                  <input id="consentement-enedis" ref="comboConsentement" type="text" class="form-input form-select consent-select"
                    role="combobox" aria-autocomplete="list" :aria-expanded="listeConsentementsOuverte ? 'true' : 'false'"
                    aria-controls="consentement-liste" autocomplete="off" :data-choix="choixConsentement"
                    :value="listeConsentementsOuverte ? rechercheConsentement : libelleConsentementChoisi"
                    :placeholder="libelleConsentementChoisi || 'Choisissez ou recherchez (nom, e-mail, PRM)'"
                    :disabled="pdlStatus === 'fetching'"
                    @focus="ouvrirListeConsentements" @click="ouvrirListeConsentements"
                    @input="rechercheConsentement = $event.target.value; listeConsentementsOuverte = true; consentementSurvole = 0"
                    @keydown.down.prevent="survolerConsentement(1)" @keydown.up.prevent="survolerConsentement(-1)"
                    @keydown.enter.prevent="validerConsentementSurvole" @keydown.esc="fermerListeConsentements"
                    @blur="fermerListeConsentementsBientot" />
                  <ul v-if="listeConsentementsOuverte" id="consentement-liste" class="consent-liste" role="listbox">
                    <li v-for="(o, i) in optionsConsentement" :key="o.cle" role="option" class="consent-option"
                      :class="{ 'consent-option--survol': i === consentementSurvole, 'consent-option--choisie': o.cle === choixConsentement }"
                      :aria-selected="o.cle === choixConsentement ? 'true' : 'false'" :data-cle="o.cle"
                      @mousedown.prevent="selectionnerConsentement(o)" @mouseenter="consentementSurvole = i">{{ o.libelle }}</li>
                    <li v-if="!optionsConsentement.length" class="consent-option consent-option--vide">
                      {{ consentementsRecus.length ? 'Aucun consentement ne correspond à « ' + rechercheConsentement + ' ».' : 'Aucun consentement reçu pour l\'instant.' }}
                    </li>
                  </ul>
                </div>
                <button type="button" class="btn-consent-outil" title="Actualiser la liste" :disabled="pdlStatus === 'fetching'" @click="chargerConsentementsRecus">&#8635;</button>
              </div>
              <button type="button" class="btn-nouvelle-demande" :disabled="demandeEnCours" @click="nouvelleDemande">
                {{ demandeEnCours ? 'Création…' : '+ Nouvelle demande de consentement' }}
              </button>
              <div v-if="demandeErreur" class="csv-status csv-status--err">{{ demandeErreur }}</div>
              <div class="form-hint">Quand le client a donné son accord, actualisez la liste.</div>
            </div>
            <div v-if="pdlStatus === 'error'" class="import-action-row">
              <div class="csv-status csv-status--err">{{ pdlErrorMessage }}</div>
              <button v-if="pdlPeutReessayer" class="btn-enedis-retry" :disabled="pdlRetryLoading" @click="reessayerSemainesManquantes">
                Réessayer maintenant
              </button>
            </div>
            <!-- Récupération en cours -->
            <div v-if="pdlStatus === 'fetching'" class="pdl-status-block">
              <div class="pdl-status-icon pdl-status-icon--fetching">&#8635;</div>
              <div class="pdl-status-content">
                <div class="pdl-status-label">Récupération des données...</div>
                <div class="pdl-progress-bar">
                  <div class="pdl-progress-fill" :style="{ width: pdlProgress + '%' }"></div>
                </div>
                <div class="pdl-status-hint">{{ pdlProgress }}% — Téléchargement depuis Enedis en cours</div>
              </div>
            </div>
            <!-- Données disponibles -->
            <div v-if="pdlStatus === 'done'" class="pdl-status-block pdl-status-block--done">
              <div class="pdl-status-icon pdl-status-icon--done">&#10003;</div>
              <div>
                <div class="pdl-status-label">Données disponibles</div>
                <div class="pdl-status-hint">Courbe de charge importée depuis Enedis.</div>
              </div>
            </div>
            <!-- étude rouverte : la courbe du consentement a été refaite depuis la copie de l'étude (étude FERMIN, 20/10/2026) -->
            <div v-if="pdlStatus === 'done' && versionPlusRecente" class="pdl-warning pdl-warning--version">
              <p class="pdl-warning__text">
                Une version plus récente de la courbe de ce consentement est disponible<template v-if="versionPlusRecente.date"> (du {{ dateFr(versionPlusRecente.date) }})</template>.
                L'étude utilise encore l'ancienne version.
              </p>
              <button type="button" class="btn-enedis-retry btn-version-courbe" @click="utiliserVersionPlusRecente">Utiliser cette version</button>
            </div>
            <!-- Semaines estimées : refus passager d'Enedis (nouvel essai possible) ou absence de mesures -->
            <div v-if="pdlStatus === 'done' && pdlAvertissementTemporaire" class="pdl-warning pdl-warning--temporaire">
              <p class="pdl-warning__text">{{ pdlAvertissementTemporaire }}</p>
              <button v-if="pdlPeutReessayer" class="btn-enedis-retry" :disabled="pdlRetryLoading" @click="reessayerSemainesManquantes">
                Réessayer les semaines manquantes
              </button>
            </div>

          </div>
        </div>

        <!-- Option 2 : Import CSV -->
        <div class="radio-option" :class="{ active: form.profileSource === 'csv' }">
          <label class="radio-label">
            <input type="radio" v-model="form.profileSource" value="csv" />
            Importer un profil de consommation
          </label>
          <p class="radio-hint">Import d'un fichier CSV de courbe de charge</p>
          <div v-if="form.profileSource === 'csv'" class="radio-content">
            <div class="file-input-wrapper">
              <input
                ref="csvInput"
                type="file"
                accept=".csv,.txt"
                style="display:none"
                @change="onFileSelect"
              />
              <button class="btn-browse" @click="$refs.csvInput.click()">Parcourir...</button>
              <!-- courbe reprise avec son nom : affiché une seule fois, dans « Courbe importée » ci-dessous -->
              <span class="file-name" :title="csvFileName">{{ csvFileName || (consumptionCurveId ? (nomFichierCourbe ? '' : '(courbe reprise)') : 'Aucun fichier sélectionné.') }}</span>
            </div>
            <!-- infos lues dans le fichier au-dessus du texte d'aide (13/10/2026) -->
            <!-- Unité demandée : le fichier ne l'indique pas (réponse 422) — choisir relance l'import -->
            <div v-if="csvUploadStatus === 'unit'" class="unit-request">
              <p class="unit-request__msg">{{ csvErrorMessage }}</p>
              <!-- unité la plus vraisemblable (ordre de grandeur des valeurs) : présélectionnée, à valider -->
              <p v-if="uniteProposee" class="unit-request__suggest">
                Unité proposée : <b>{{ uniteProposee.unit }}</b>. {{ uniteProposee.reason }}
              </p>
              <label class="form-label" for="csv-unit-request">Unité des valeurs du fichier</label>
              <div class="unit-request__row">
                <select id="csv-unit-request" v-model="form.uniteCsv" class="form-input form-select" :class="{ 'form-select--attention': !form.uniteCsv }" @change="uploadCsv">
                  <option :value="null" disabled>— Choisir l'unité —</option>
                  <option v-for="u in unitOptions" :key="u.value" :value="u.value">{{ u.label }}</option>
                </select>
                <button v-if="uniteProposee && form.uniteCsv" type="button" class="btn-unit-validate" @click="uploadCsv">
                  Valider ({{ form.uniteCsv }})
                </button>
              </div>
            </div>
            <!-- Plusieurs compteurs dans le fichier (export Enedis multi-PRM, réponse 422) : un clic sur un compteur
                 l'importe ; plusieurs cochés → somme de leurs courbes (regroupement de points de livraison, 14/10/2026) -->
            <div v-if="csvUploadStatus === 'compteur'" class="unit-request choix-compteur">
              <p class="unit-request__msg">{{ csvErrorMessage }}</p>
              <div v-for="c in compteursFichier" :key="c.prm" class="compteur-ligne">
                <input type="checkbox" class="compteur-coche" :value="c.prm" v-model="compteursCoches"
                       :title="'Cocher pour additionner ' + c.prm + ' à d\'autres compteurs'" />
                <button type="button" class="compteur-option" :data-prm="c.prm" @click="choisirCompteur(c.prm)">
                  <b>{{ c.prm }}</b>
                  <span v-if="c.debut"> · du {{ c.debut }} au {{ c.fin }}</span>
                  <span v-if="c.pasMinutes"> · pas {{ c.pasMinutes }} min</span>
                  <span v-if="c.kwhAn != null"> · ≈ {{ formatNumber(Math.round(c.kwhAn)) }} kWh/an</span>
                </button>
              </div>
              <button v-if="compteursCoches.length > 1" type="button" class="btn-unit-validate btn-somme-compteurs" @click="additionnerCompteurs">
                Additionner les {{ compteursCoches.length }} compteurs cochés<span v-if="kwhCoches != null"> (≈ {{ formatNumber(Math.round(kwhCoches)) }} kWh/an)</span>
              </button>
            </div>
            <div v-if="csvUploadStatus === 'saved'" class="csv-status csv-status--ok">Courbe importée avec succès</div>
            <div v-if="csvUploadStatus === 'saved' && importInfo" class="import-info">
              <!-- &nbsp; : Vue supprime l'espace entre deux éléments séparés par un retour à la ligne -->
              <span>{{ importInfo.unitDetected ? 'Lu dans le fichier : unité' : 'Unité choisie :' }} <b>{{ importInfo.unit }}</b>&nbsp;</span>
              <a href="#" class="import-info__change" @click.prevent="showUnitChange = !showUnitChange">(changer)</a>
              <span> · {{ pasLabel }}</span>
              <span v-if="importInfo.nbCompteurs > 1"> · {{ importInfo.prm && importInfo.prm.indexOf(' + ') >= 0 ? 'somme des compteurs' : 'compteur' }} <b>{{ importInfo.prm }}</b>&nbsp;</span>
              <a v-if="importInfo.nbCompteurs > 1" href="#" class="import-info__change" @click.prevent="uploadCsvAutreCompteur">(changer)</a>
              <span v-if="importInfo.readValues"> · {{ formatNumber(importInfo.readValues) }} valeurs</span>
              <span v-if="importInfo.ignoredLines" class="import-info__warn"> · {{ importInfo.ignoredLines }} ligne{{ importInfo.ignoredLines > 1 ? 's' : '' }} illisible{{ importInfo.ignoredLines > 1 ? 's' : '' }} ignorée{{ importInfo.ignoredLines > 1 ? 's' : '' }}</span>
              <div v-if="showUnitChange" class="import-info__unit">
                <label class="form-label" for="csv-unit-change">Autre unité (le fichier sera réimporté)</label>
                <select id="csv-unit-change" v-model="form.uniteCsv" class="form-input form-select" @change="uploadCsv">
                  <option :value="null" disabled>— Choisir l'unité —</option>
                  <option v-for="u in unitOptions" :key="u.value" :value="u.value">{{ u.label }}</option>
                </select>
              </div>
            </div>
            <div v-if="csvUploadStatus === 'error'" class="csv-status csv-status--err">{{ csvErrorMessage }}</div>
            <div v-if="csvUploadStatus === 'uploading'" class="pdl-status-block">
              <div class="pdl-status-icon pdl-status-icon--fetching">&#8635;</div>
              <div class="pdl-status-content">
                <div class="pdl-status-label">Import en cours...</div>
                <div class="pdl-progress-bar">
                  <div class="pdl-progress-fill" :style="{ width: csvUploadProgress + '%' }"></div>
                </div>
                <div class="pdl-status-hint">{{ csvUploadProgress }}% — Envoi et traitement du fichier CSV</div>
              </div>
            </div>
            <!-- nom du fichier importé (23/10/2026) ; étude enregistrée avant : nom inconnu -->
            <div v-if="csvUploadStatus === 'none' && consumptionCurveId && !csvFileName" class="csv-status csv-status--ok courbe-reprise">
              <template v-if="nomFichierCourbe"><span class="courbe-reprise__libelle">Courbe importée :</span> <span class="courbe-reprise__nom" :title="nomFichierCourbe">{{ nomFichierCourbe }}</span></template>
              <template v-else>Courbe de charge disponible (sauvegardée précédemment)</template>
            </div>
            <p class="csv-hints">
              Le format, l'unité et le pas de temps sont reconnus automatiquement dès que le fichier est choisi.<br />
              Fichiers acceptés :<br />
              &bull; les <strong>exports Enedis</strong> (R63, SGE, M021) et les <strong>exports horaires PV*SOL</strong>, tels quels ;<br />
              &bull; tout fichier avec une colonne <strong>date et heure</strong> et une colonne de <strong>valeurs</strong>, comme le <a href="/modele-courbe-de-charge.csv" download class="lien-modele">modèle RETIX</a>
              (<code>date;heure;puissance_kW</code>).<br />
              Si l'unité n'est pas indiquée dans le fichier, elle vous sera proposée.
            </p>
          </div>
        </div>

        <!-- Option 3 : Profil prédéfini -->
        <div class="radio-option" :class="{ active: form.profileSource === 'predefined' }">
          <label class="radio-label">
            <input type="radio" v-model="form.profileSource" value="predefined" />
            Choisir un profil prédéfini
          </label>
          <p class="radio-hint">Choix dans une liste d'un profil de consommation</p>
          <div v-if="form.profileSource === 'predefined'" class="radio-content">
            <select v-model="form.profilPredifini" class="form-input form-select" @change="predefinedStatsStatus = 'none'">
              <option value="maison_4pers">Maison 4 personnes (~4 500 kWh/an)</option>
              <option value="maison_2pers">Maison 2 personnes (~3 000 kWh/an)</option>
              <option value="appart_4pers">Appartement 4 personnes (~3 000 kWh/an)</option>
              <option value="appart_2pers">Appartement 2 personnes (~2 000 kWh/an)</option>
            </select>
            <div class="import-action-row">
              <button
                class="btn-import"
                :class="{ 'btn-import--loading': predefinedStatsStatus === 'loading' }"
                :disabled="predefinedStatsStatus === 'loading'"
                @click="loadPredefinedStats"
              >
                {{ predefinedStatsStatus === 'loading' ? 'Import en cours...' : 'Importer' }}
              </button>
              <div v-if="predefinedStatsStatus === 'ok'" class="csv-status csv-status--ok">Bilan calculé</div>
              <div v-if="predefinedStatsStatus === 'error'" class="csv-status csv-status--err">Erreur lors du calcul</div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Erreur de calcul du bilan (sinon l'échec est totalement invisible) -->
    <div v-if="statsErrorMessage && !curveStats" class="csv-status csv-status--err">
      {{ statsErrorMessage }}
    </div>

    <!-- Panneau Bilan courbe de charge -->
    <div v-if="curveStats" class="bilan-panel">
      <div class="bilan-panel__header">
        <div class="bilan-panel__title">Bilan courbe de charge</div>
        <button class="btn-export-csv" @click="exportCsv" :disabled="csvExportLoading">
          {{ csvExportLoading ? 'Export...' : '⬇ Exporter CSV (10 min)' }}
        </button>
      </div>

      <!-- KPIs -->
      <div class="bilan-kpis">
        <div class="bilan-kpi">
          <div class="bilan-kpi__label">Consommation annuelle</div>
          <div class="bilan-kpi__value">{{ formatKwh(curveStats.annualKwh) }}</div>
          <div class="bilan-kpi__unit">kWh/an</div>
        </div>
        <!-- tarif unique : pas d'heures creuses, cadre masqué (12/10/2026) -->
        <div v-if="form.typeTarif !== 'unique'" class="bilan-kpi">
          <template v-if="avecHsc">
            <div class="bilan-kpi__label">Heures creuses / super creuses</div>
            <div class="bilan-kpi__value bilan-kpi__value--hsc">{{ curveStats.offPeakPercent }} % · {{ curveStats.superOffPeakPercent || 0 }} %</div>
            <div class="bilan-kpi__unit">en HC · en HSC</div>
          </template>
          <template v-else>
            <div class="bilan-kpi__label">Part en heures creuses</div>
            <div class="bilan-kpi__value">{{ curveStats.offPeakPercent }}</div>
            <div class="bilan-kpi__unit">% de la consommation</div>
          </template>
        </div>
        <div class="bilan-kpi">
          <div class="bilan-kpi__label">Puissance moy. soutirée</div>
          <div class="bilan-kpi__value">{{ formatKw(curveStats.avgPowerKw) }}</div>
          <div class="bilan-kpi__unit">kW</div>
        </div>
        <div class="bilan-kpi">
          <div class="bilan-kpi__label">Puissance max. soutirée</div>
          <div class="bilan-kpi__value">{{ formatKw(curveStats.maxPowerKw) }}</div>
          <div class="bilan-kpi__unit">kW</div>
        </div>
        <div class="bilan-kpi bilan-kpi--highlight">
          <div class="bilan-kpi__label">Estimation facture annuelle</div>
          <div class="bilan-kpi__value">{{ formatEur(estimationFacture) }}</div>
          <div class="bilan-kpi__unit">€ {{ taxeMention }}/an</div>
        </div>
      </div>

      <!-- Qualité des données, à part des indicateurs : vert 100 % réelles, orange ≥ 75 %, rouge < 75 % (06/10/2026) -->
      <div class="bilan-qualite" :class="'bilan-qualite--' + niveauQualite">
        <span class="bilan-qualite__label">Données réelles</span>
        <span class="bilan-qualite__value">{{ formatPercent(realPointsPercent) }} %</span>
        <span v-if="niveauQualite !== 'vert'" class="bilan-qualite__detail">· {{ formatPercent(100 - realPointsPercent) }} % extrapolées</span>
      </div>

      <alerte-extrapolation :stats="curveStats" />

      <!-- Graphique Chart.js -->
      <consumption-chart
        :curve-id="consumptionCurveId"
        :curve-stats="curveStats"
      />
    </div>

    <!-- Demande de consentement créée depuis l'étude (12/10/2026) : lien et e-mail au client -->
    <div v-if="demandeCreee" class="demande-modale" role="dialog" aria-modal="true" aria-labelledby="demande-titre" @click.self="demandeCreee = null">
      <div class="demande-modale__boite">
        <div id="demande-titre" class="demande-modale__titre">Demande de consentement créée</div>
        <p class="demande-modale__texte">{{ demandeCreee.description }} · {{ demandeCreee.email }} · 3 mois</p>
        <label class="form-label" for="demande-lien">Lien à transmettre au client</label>
        <div class="consent-choix">
          <input id="demande-lien" :value="demandeLien" readonly class="form-input" @focus="$event.target.select()" />
          <button type="button" class="btn-consent-outil" title="Copier le lien" @click="copierLienDemande">{{ lienCopie ? '✓' : '⧉' }}</button>
        </div>
        <div class="demande-modale__actions">
          <button type="button" class="btn-cancel-small" @click="copierMessageDemande">{{ messageCopie ? 'Message copié ✓' : 'Copier le message' }}</button>
          <a :href="demandeMailto" class="btn-enedis demande-modale__mail">Envoyer par e-mail au client</a>
          <button type="button" class="btn-cancel-small" @click="demandeCreee = null">Fermer</button>
        </div>
      </div>
    </div>

    <div class="step-footer">
      <button class="btn-back" @click="$emit('back')">Retour</button>
      <button class="btn-next" :disabled="!isValid" @click="submit">Étape suivante →</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import axios from 'axios'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'
import ConsumptionChart from './ConsumptionChart.vue'
import AlerteExtrapolation from './AlerteExtrapolation.vue'
import { lienDemarrage, mailtoConsentement, copierMessageConsentement } from '@/utils/mailConsentement'
import { raccordementParDefaut, TVA_ELECTRICITE } from '@/store/modules/wizard.module'

export default {
  name: 'Step3Consommation',
  components: { ConsumptionChart, AlerteExtrapolation },
  data() {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const oneYearAgo = new Date(yesterday)
    oneYearAgo.setFullYear(yesterday.getFullYear() - 1)
    const toDateStr = (d) => d.toISOString().slice(0, 10)
    return {
      form: {
        typeTarif: 'hchp',
        puissanceAbonnement: null,
        raccordement: null,
        profileSource: 'csv',
        profilPredifini: 'maison_4pers',
        csvFile: null,
        uniteCsv: null, // null = unité lue dans le fichier ; renseignée seulement si l'utilisateur la choisit
        pdlNumber: '',
        tarifAbonnementAnnuel: null,
        tarifHP: null,
        tarifHC: null,
        tarifHPHiver: null,
        tarifHCHiver: null,
        tarifHPEte: null,
        tarifHCEte: null,
        taxeelec: null,
        periodesHC: [{ debut: '22:00', fin: '06:00' }],
        // heures super creuses (lot 8) : prix et plages, toute l'année ou par saison
        tarifHSC: null,
        tarifHSCHiver: null,
        tarifHSCEte: null,
        periodesHSC: [],
        periodesHCEte: [],
        periodesHCHiver: [],
        periodesHSCEte: [],
        periodesHSCHiver: [],
      },
      csvFileName: '',
      csvUploadStatus: 'none', // 'none' | 'uploading' | 'unit' (unité à choisir) | 'saved' | 'error'
      csvErrorMessage: '',
      uniteProposee: null, // { unit, reason } : unité proposée par le serveur quand le fichier ne l'indique pas
      importInfo: null, // unité, pas, valeurs lues : renvoyés par l'import
      showUnitChange: false,
      unitOptions: [
        { value: 'W', label: 'W (puissance en watts)' },
        { value: 'kW', label: 'kW (puissance en kilowatts)' },
        { value: 'Wh', label: 'Wh (énergie par pas de temps)' },
        { value: 'kWh', label: 'kWh (énergie par pas de temps)' },
      ],
      csvUploadProgress: 0,
      predefinedStatsStatus: 'none', // 'none' | 'loading' | 'ok' | 'error'
      csvExportLoading: false,
      statsLoading: false,
      statsErrorMessage: '',
      formReady: false,
      isDirty: false,
      estimationFacture: 0,
      curveStats: null,
      // PDL Enedis
      pdlStatus: 'idle',   // idle | waiting | fetching | done | error
      pdlProgress: 0,
      pdlErrorMessage: '',
      pdlAvertissementTemporaire: '', // semaines refusées par Enedis pour une raison passagère (estimées)
      pdlAvertissementSansDonnees: '', // périodes sans mesure chez Enedis (estimées)
      pdlPeutReessayer: false,
      pdlRetryLoading: false,
      pdlConsentCode: '',
      pdlPollTimer: null,
      pdlPollEnCours: false,
      consentementsRecus: [],
      choixConsentement: '', // « code|prm » du consentement choisi
      versionPlusRecente: null, // { date } quand la courbe du consentement a changé depuis la copie de l'étude
      rechercheConsentement: '',
      listeConsentementsOuverte: false,
      consentementSurvole: 0,
      demandeEnCours: false,
      demandeErreur: '',
      demandeCreee: null, // demande de consentement créée depuis cette étape
      lienCopie: false,
      messageCopie: false,
      prmCsv: null, // compteur choisi quand le fichier en contient plusieurs
      compteursFichier: [],
      compteursCoches: [], // compteurs à additionner
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (!this.formReady) return
        this.SET_DIRTY(true)
      },
    },
    'form.profileSource'(src) {
      if (src === 'pdl') this.chargerConsentementsRecus()
    },
    // nouvelle puissance souscrite : raccordement pré-rempli de nouveau (monophasé ≤ 12 kVA, triphasé au-delà)
    'form.puissanceAbonnement'(kva) {
      if (!this.formReady || this.estPro) return
      const r = raccordementParDefaut(kva)
      if (r) this.form.raccordement = r
    },
  },
  computed: {
    kwhCoches() {
      const c = this.compteursFichier.filter(x => this.compteursCoches.indexOf(x.prm) >= 0)
      return c.length && c.every(x => x.kwhAn != null) ? c.reduce((s, x) => s + x.kwhAn, 0) : null
    },
    /** Prix au kWh visibles et inférieurs à 1 c€ : saisis en € au lieu de c€ (le plus bas des tarifs réels dépasse 5 c€). */
    tarifsEnEuros() {
      const f = this.form
      const t = f.typeTarif
      const champs = []
      if (t === 'unique') champs.push(['tarifHP', 'tarif'])
      if (t === 'hchp' || t === 'hsc') champs.push(['tarifHP', 'HP'], ['tarifHC', 'HC'])
      if (t === 'hsc') champs.push(['tarifHSC', 'HSC'])
      if (t === 'saisonnier' || t === 'hsc_saisonnier') {
        champs.push(['tarifHPHiver', 'HP hiver'], ['tarifHCHiver', 'HC hiver'], ['tarifHPEte', 'HP été'], ['tarifHCEte', 'HC été'])
      }
      if (t === 'hsc_saisonnier') champs.push(['tarifHSCHiver', 'HSC hiver'], ['tarifHSCEte', 'HSC été'])
      champs.push(['taxeelec', 'taxe'])
      return champs
        .filter(([cle]) => typeof f[cle] === 'number' && f[cle] > 0 && f[cle] < 1)
        .map(([cle, libelle]) => ({ cle, libelle, valeur: f[cle] }))
    },
    // consommation via PDL (Data Connect) : particuliers seulement (13/10/2026)
    pdlDisponible() { return this.clientType !== 'Professionnel' },
    estPro() { return this.clientType === 'Professionnel' },
    /** Listes de plages horaires affichées selon le type de tarif (HC ; HSC ; par saison pour « hsc_saisonnier »). */
    listesPlages() {
      const t = this.form.typeTarif
      const hc = { cle: 'periodesHC', titre: 'Périodes Heures Creuses', court: 'HC', min: 1 }
      if (t === 'unique') return []
      if (t === 'hsc') return [hc, { cle: 'periodesHSC', titre: 'Périodes Heures Super Creuses', court: 'HSC', min: 0 }]
      if (t === 'hsc_saisonnier') {
        return [
          { cle: 'periodesHCEte', titre: 'Été (01/04 – 31/10) — Heures Creuses', court: 'HC', min: 0 },
          { cle: 'periodesHSCEte', titre: 'Été (01/04 – 31/10) — Heures Super Creuses', court: 'HSC', min: 0 },
          { cle: 'periodesHCHiver', titre: 'Hiver (01/11 – 31/03) — Heures Creuses', court: 'HC', min: 0 },
          { cle: 'periodesHSCHiver', titre: 'Hiver (01/11 – 31/03) — Heures Super Creuses', court: 'HSC', min: 0 },
        ]
      }
      return [hc]
    },
    avecHsc() { return this.form.typeTarif === 'hsc' || this.form.typeTarif === 'hsc_saisonnier' },
    /** Couleur de l'indicateur « Données réelles » : vert (100 %), orange (≥ 75 %), rouge (< 75 %). */
    niveauQualite() {
      const p = this.realPointsPercent
      if (p >= 99.95) return 'vert'
      return p >= 75 ? 'orange' : 'rouge'
    },
    realPointsPercent() {
      const st = this.curveStats
      return st && st.realPointsPercent != null ? st.realPointsPercent : 100
    },
    recopieWarnings() {
      const st = this.curveStats
      return st && st.warnings ? st.warnings.filter(w => w.code === 'RECOPIE_PROFIL_CLIENT') : []
    },
    ...mapState('wizard', [
      'projectName', 'clientType',
      'typeTarif', 'puissanceAbonnement', 'raccordement', 'profileSource', 'profilPredifini',
      'csvFile', 'tarifAbonnementAnnuel', 'tarifHP', 'tarifHC',
      'tarifHPHiver', 'tarifHCHiver', 'tarifHPEte', 'tarifHCEte',
      'taxeelec', 'periodesHC', 'tarifHSC', 'tarifHSCHiver', 'tarifHSCEte',
      'periodesHSC', 'periodesHCEte', 'periodesHCHiver', 'periodesHSCEte', 'periodesHSCHiver',
      'consumptionCurveId', 'consumptionCurveStatus', 'nomFichierCourbe',
      'pdlNumber', 'enedisConsentCode',
      'clientFirstName', 'clientLastName', 'raisonSociale', 'clientEmail',
    ]),
    /** Une ligne par consentement et par compteur (un consentement « entreprise » peut en couvrir plusieurs). */
    optionsConsentement() {
      const options = []
      const sansAccents = t => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      const mots = sansAccents(this.rechercheConsentement).split(/\s+/).filter(Boolean)
      // les plus récents en haut (12/10/2026)
      const recus = this.consentementsRecus.slice().sort((a, b) => String(b.dateConsentement || '').localeCompare(String(a.dateConsentement || '')))
      recus.forEach(c => {
        (c.prms || []).forEach(prm => {
          const cle = c.code + '|' + prm
          const texte = sansAccents([c.description, c.email, c.auteur, prm, c.code].join(' '))
          // recherche par nom, e-mail, PRM ou auteur ; le consentement choisi reste toujours dans la liste
          if (mots.length && cle !== this.choixConsentement && !mots.every(m => texte.includes(m))) return
          const etat = c.prmCourbe === prm ? this.libelleCourbe(c) : 'À télécharger'
          const qui = c.description || c.email || c.code
          let libelle = qui + ' · PRM ' + prm + ' · consenti le ' + this.dateFr(c.dateConsentement) + ' · ' + etat
          if (c.auteur) libelle += ' · ' + c.auteur
          options.push({ cle, code: c.code, prm, consentement: c, libelle })
        })
      })
      // étude rouverte : le consentement utilisé reste affiché, même s'il n'est plus dans la liste (expiré, autre auteur)
      if (this.choixConsentement && !options.some(o => o.cle === this.choixConsentement)) {
        const [code, prm] = this.choixConsentement.split('|')
        options.unshift({ cle: this.choixConsentement, code, prm, consentement: null, libelle: 'Consentement utilisé pour cette étude · PRM ' + prm })
      }
      return options
    },
    libelleConsentementChoisi() {
      const o = this.optionsConsentement.find(x => x.cle === this.choixConsentement)
      return o ? o.libelle : ''
    },
    /** Description d'une demande créée depuis l'étude : client et projet de la Step 1. */
    descriptionDemande() {
      const nom = this.clientType === 'Particulier'
        ? [this.clientFirstName, this.clientLastName].filter(Boolean).join(' ')
        : (this.raisonSociale || [this.clientFirstName, this.clientLastName].filter(Boolean).join(' '))
      return [nom, this.projectName].filter(Boolean).join(' — ')
    },
    demandeLien() {
      return this.demandeCreee ? lienDemarrage(this.demandeCreee.code) : ''
    },
    demandeMailto() {
      return this.demandeCreee ? mailtoConsentement(this.demandeCreee.email, this.demandeLien, authenticationService.getAgence()) : ''
    },
    isValid() {
      // un fichier choisi mais pas (encore) importé ne suffit pas : le calcul doit partir de la courbe importée (B28)
      if (this.form.profileSource === 'csv' && (!this.consumptionCurveId || ['uploading', 'unit', 'compteur', 'error'].includes(this.csvUploadStatus))) return false
      if (this.form.profileSource === 'pdl' && !this.consumptionCurveId) return false
      return true
    },
    pasLabel() {
      const i = this.importInfo
      if (!i) return ''
      if (i.sourceIntervalsMinutes && i.sourceIntervalsMinutes.length > 1 && i.prm && i.prm.indexOf(' + ') >= 0) {
        return 'pas ' + i.sourceIntervalsMinutes.join(' et ') + ' min' // un pas par compteur additionné
      }
      if (i.sourceIntervalsMinutes && i.sourceIntervalsMinutes.length > 1) {
        return 'pas variable : ' + i.sourceIntervalsMinutes.join(' et ') + ' min'
      }
      return 'pas ' + this.formatInterval(i.sourceIntervalMinutes)
    },
    taxeMention() {
      return this.clientType === 'Particulier' ? 'TTC' : 'HT'
    },
    /** Prix saisis HT ; TVA ajoutée pour un particulier (règle du 12/10/2026, comme WizardService.facteurTvaElectricite). */
    facteurTvaElec() {
      return this.clientType === 'Particulier' ? 1 + TVA_ELECTRICITE : 1
    },
  },
  mounted() {
    this.form.typeTarif = this.typeTarif || 'hchp'
    this.form.puissanceAbonnement = this.puissanceAbonnement
    this.form.raccordement = this.estPro ? 'tri' : this.raccordement || raccordementParDefaut(this.puissanceAbonnement)
    // source par défaut (13/10/2026) : consentement Enedis (PDL) pour un particulier, import de fichier pour un pro
    this.form.profileSource = this.profileSource || (this.pdlDisponible ? 'pdl' : 'csv')
    // un professionnel ne peut plus passer par le PDL (13/10/2026) : sans courbe déjà chargée, retour à l'import de fichier
    if (this.form.profileSource === 'pdl' && !this.pdlDisponible && !this.consumptionCurveId) this.form.profileSource = 'csv'
    this.form.profilPredifini = this.profilPredifini || 'maison_4pers'
    this.form.tarifAbonnementAnnuel = this.tarifAbonnementAnnuel
    this.form.tarifHP = this.tarifHP
    this.form.tarifHC = this.tarifHC
    this.form.tarifHPHiver = this.tarifHPHiver
    this.form.tarifHCHiver = this.tarifHCHiver
    this.form.tarifHPEte = this.tarifHPEte
    this.form.tarifHCEte = this.tarifHCEte
    this.form.taxeelec = this.taxeelec
    if (this.periodesHC && this.periodesHC.length > 0) {
      this.form.periodesHC = JSON.parse(JSON.stringify(this.periodesHC))
    }
    this.form.tarifHSC = this.tarifHSC
    this.form.tarifHSCHiver = this.tarifHSCHiver
    this.form.tarifHSCEte = this.tarifHSCEte
    ;['periodesHSC', 'periodesHCEte', 'periodesHCHiver', 'periodesHSCEte', 'periodesHSCHiver'].forEach(k => {
      if (this[k]) this.form[k] = JSON.parse(JSON.stringify(this[k]))
    })
    if (this.csvFile) {
      this.form.csvFile = this.csvFile
      this.csvFileName = this.csvFile.name
    }
    if (this.pdlNumber) this.form.pdlNumber = this.pdlNumber
    if (this.enedisConsentCode && this.pdlNumber) this.choixConsentement = this.enedisConsentCode + '|' + this.pdlNumber
    // Reprendre un polling en cours si le consentement a déjà été initié
    if (this.enedisConsentCode && this.form.profileSource === 'pdl') {
      this.pdlConsentCode = this.enedisConsentCode
      if (this.consumptionCurveId) {
        this.pdlStatus = 'done'
        this.chargerAvertissementsEnedis()
        this.verifierVersionCourbe()
      } else {
        this.pdlStatus = 'waiting'
        this.startPolling()
      }
    }
    if (this.consumptionCurveId) {
      this.recomputeStats()
    }
    if (this.form.profileSource === 'pdl') this.chargerConsentementsRecus()
    this.$nextTick(() => { this.formReady = true })
  },
  beforeDestroy() {
    // une réponse arrivée après le départ de l'étape est ignorée : le store est peut-être déjà celui d'une autre étude
    // (22/10/2026 : import d'un CSV, retour à l'accueil, nouvelle étude → le CSV arrivait dans la nouvelle étude)
    this.ecranQuitte = true
    this.stopPolling()
    if (this.formReady) {
      this.SET_STEP3({
        typeTarif: this.form.typeTarif,
        puissanceAbonnement: this.form.puissanceAbonnement,
        raccordement: this.form.raccordement,
        profileSource: this.form.profileSource,
        profilPredifini: this.form.profilPredifini,
        tarifAbonnementAnnuel: this.form.tarifAbonnementAnnuel,
        tarifHP: this.form.tarifHP,
        tarifHC: this.form.tarifHC,
        tarifHPHiver: this.form.tarifHPHiver,
        tarifHCHiver: this.form.tarifHCHiver,
        tarifHPEte: this.form.tarifHPEte,
        tarifHCEte: this.form.tarifHCEte,
        taxeelec: this.form.taxeelec,
        periodesHC: this.form.periodesHC ? JSON.parse(JSON.stringify(this.form.periodesHC)) : [],
        ...this.champsHsc(),
        pdlNumber: this.form.pdlNumber,
        consommationAnnuelleKwh: this.curveStats ? this.curveStats.annualKwh : 0,
        estimationFactureAnnuelle: this.estimationFacture,
      })
    }
  },
  methods: {
    nombreFr(v) {
      return Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 5 })
    },
    /** Somme des compteurs cochés : un seul import, le serveur additionne leurs courbes au pas de 10 min. */
    additionnerCompteurs() {
      this.prmCsv = this.compteursCoches.join(',')
      this.uploadCsv()
    },
    choisirCompteur(prm) {
      this.prmCsv = prm
      this.uploadCsv()
    },
    /** Autre compteur du même fichier : la liste est redemandée au serveur (import sans compteur choisi). */
    uploadCsvAutreCompteur() {
      this.prmCsv = null
      this.uploadCsv()
    },
    corrigerTarifsEnEuros() {
      this.tarifsEnEuros.forEach(t => {
        this.form[t.cle] = Math.round(t.valeur * 100 * 100000) / 100000
      })
      this.calculateEstimation()
    },
    ...mapMutations('wizard', ['SET_DIRTY', 'SET_STEP3', 'SET_CONSUMPTION_CURVE_ID']),
    markDirty() {
      if (this.curveStats) this.isDirty = true
    },
    calculateEstimation() {
      if (!this.curveStats) { this.estimationFacture = 0; return }
      const kwh = this.curveStats.annualKwh
      const abonnement = this.form.tarifAbonnementAnnuel || 0
      const taxeelecCost = kwh * ((this.form.taxeelec || 0) / 100)
      if (this.form.typeTarif === 'unique') {
        this.estimationFacture = (kwh * (this.form.tarifHP / 100) + taxeelecCost + abonnement) * this.facteurTvaElec
        return
      }
      const hcRatio = this.curveStats.offPeakPercent / 100
      if (this.form.typeTarif === 'saisonnier') {
        // mêmes saisons que la simulation (B37) : été du 01/04 au 31/10, hiver du 01/11 au 31/03
        const s = this.saisons()
        this.estimationFacture = (
          (s.hiver - s.hiverHC) * (this.form.tarifHPHiver / 100)
          + s.hiverHC * (this.form.tarifHCHiver / 100)
          + (s.ete - s.eteHC) * (this.form.tarifHPEte / 100)
          + s.eteHC * (this.form.tarifHCEte / 100)
          + taxeelecCost + abonnement) * this.facteurTvaElec
        return
      }
      if (this.form.typeTarif === 'hsc' || this.form.typeTarif === 'hsc_saisonnier') {
        // heures super creuses (lot 8) : kWh HP / HC / HSC de chaque saison, plages de la saison comprises
        const s = this.saisons()
        const f = this.form
        const annuel = f.typeTarif === 'hsc'
        const prix = {
          hiver: annuel ? [f.tarifHP, f.tarifHC, f.tarifHSC] : [f.tarifHPHiver, f.tarifHCHiver, f.tarifHSCHiver],
          ete: annuel ? [f.tarifHP, f.tarifHC, f.tarifHSC] : [f.tarifHPEte, f.tarifHCEte, f.tarifHSCEte],
        }
        const saison = (kwhSaison, hc, hsc, p) => (kwhSaison - hc - hsc) * (p[0] || 0) / 100 + hc * (p[1] || 0) / 100 + hsc * (p[2] || 0) / 100
        this.estimationFacture = (saison(s.hiver, s.hiverHC, s.hiverHSC, prix.hiver) + saison(s.ete, s.eteHC, s.eteHSC, prix.ete)
          + taxeelecCost + abonnement) * this.facteurTvaElec
        return
      }
      // hchp
      this.estimationFacture = (kwh * (1 - hcRatio) * (this.form.tarifHP / 100)
        + kwh * hcRatio * (this.form.tarifHC / 100)
        + taxeelecCost + abonnement) * this.facteurTvaElec
    },
    /** kWh par saison et en heures creuses ; à défaut (ancienne réponse), répartition par mois et part HC globale. */
    saisons() {
      const st = this.curveStats
      if (st.winterKwh || st.summerKwh) {
        return { hiver: st.winterKwh, hiverHC: st.winterOffPeakKwh || 0, hiverHSC: st.winterSuperOffPeakKwh || 0,
          ete: st.summerKwh, eteHC: st.summerOffPeakKwh || 0, eteHSC: st.summerSuperOffPeakKwh || 0 }
      }
      const hcRatio = (st.offPeakPercent || 0) / 100
      let hiver = 0, ete = 0
      ;(st.monthly || []).forEach((m, i) => {
        const mois = m.month || i + 1
        if (mois >= 4 && mois <= 10) ete += m.kwh || 0
        else hiver += m.kwh || 0
      })
      const brut = hiver + ete
      const echelle = brut > 0 ? st.annualKwh / brut : 0
      hiver *= echelle; ete *= echelle
      return { hiver, hiverHC: hiver * hcRatio, hiverHSC: 0, ete, eteHC: ete * hcRatio, eteHSC: 0 }
    },
    /** Champs « heures super creuses » enregistrés avec l'étape (lot 8). */
    champsHsc() {
      const f = this.form
      const copie = l => JSON.parse(JSON.stringify(l || []))
      return {
        tarifHSC: f.tarifHSC, tarifHSCHiver: f.tarifHSCHiver, tarifHSCEte: f.tarifHSCEte,
        periodesHSC: copie(f.periodesHSC), periodesHCEte: copie(f.periodesHCEte), periodesHCHiver: copie(f.periodesHCHiver),
        periodesHSCEte: copie(f.periodesHSCEte), periodesHSCHiver: copie(f.periodesHSCHiver),
      }
    },
    /** Plages tarifaires complètes envoyées au serveur pour le bilan (type de tarif, HC, HSC, plages par saison). */
    plagesJson() {
      const f = this.form
      return JSON.stringify({
        typeTarif: f.typeTarif, periodesHC: f.periodesHC || [], periodesHSC: f.periodesHSC || [],
        periodesHCEte: f.periodesHCEte || [], periodesHCHiver: f.periodesHCHiver || [],
        periodesHSCEte: f.periodesHSCEte || [], periodesHSCHiver: f.periodesHSCHiver || [],
      })
    },
    hasWarning(code) {
      return !!(this.curveStats && this.curveStats.warnings && this.curveStats.warnings.some(w => w.code === code))
    },
    formatPercent(v) {
      return (Math.round((v || 0) * 10) / 10).toLocaleString('fr-FR', { maximumFractionDigits: 1 })
    },
    formatDateFr(iso) {
      if (!iso) return ''
      const p = iso.split('-')
      return p[2] + '/' + p[1] + '/' + p[0]
    },
    async recomputeStats() {
      if (!this.consumptionCurveId) return
      this.statsLoading = true
      try {
        const token = authenticationService.status.jwtToken
        // tarif unique : pas d'heures creuses, mais le bilan complet est relu sur le serveur (B01)
        const periodesHC = this.form.typeTarif === 'unique'
          ? []
          : (this.form.periodesHC && this.form.periodesHC.length ? this.form.periodesHC : [{ debut: '22:00', fin: '06:00' }])
        // la courbe enregistrée est au pas de 10 min : aucun pas à transmettre (B10)
        const recomputeBody = { periodesHC: JSON.stringify(periodesHC), plages: this.plagesJson() }
        const response = await axios.post(
          conf.API_URL + `/wizard/curve/${this.consumptionCurveId}/stats`,
          recomputeBody,
          { headers: { Authorization: 'Bearer ' + token } }
        )
        if (response.data && response.data.data) {
          this.curveStats = response.data.data
          this.statsErrorMessage = ''
          this.calculateEstimation()
          this.isDirty = false
        }
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : "Impossible de calculer le bilan de la courbe de charge."
        this.statsErrorMessage = msg
      } finally {
        this.statsLoading = false
      }
    },
    onFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.form.csvFile = file
      this.csvFileName = file.name
      this.form.uniteCsv = null // nouveau fichier : unité relue dans le fichier
      this.prmCsv = null // et compteur redemandé s'il y en a plusieurs
      this.compteursFichier = []
      this.uniteProposee = null
      this.showUnitChange = false
      this.importInfo = null
      this.curveStats = null
      this.uploadCsv()
    },
    async uploadCsv() {
      if (!this.form.csvFile) return
      this.csvUploadStatus = 'uploading'
      this.csvUploadProgress = 0
      this.curveStats = null
      try {
        const token = authenticationService.status.jwtToken
        const formData = new FormData()
        formData.append('file', this.form.csvFile)
        formData.append('periodesHC', JSON.stringify(this.form.typeTarif === 'unique' ? [] : (this.form.periodesHC || [{ debut: '22:00', fin: '06:00' }])))
        formData.append('plages', this.plagesJson())
        if (this.form.uniteCsv) formData.append('unit', this.form.uniteCsv)
        if (this.prmCsv) formData.append('prm', this.prmCsv)
        if (this.consumptionCurveId) formData.append('oldCurveId', String(this.consumptionCurveId))
        const self = this
        const response = await axios.post(
          conf.API_URL + '/wizard/curve',
          formData,
          {
            headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' },
            onUploadProgress: function(event) {
              if (event.total) {
                self.csvUploadProgress = Math.min(90, Math.round((event.loaded / event.total) * 90))
              }
            },
          }
        )
        if (this.ecranQuitte) return
        const stats = response.data.data
        this.csvUploadProgress = 100
        this.SET_CONSUMPTION_CURVE_ID({ id: stats.curveId, status: 'saved', nomFichier: this.csvFileName })
        this.oublierConsentement()
        this.curveStats = stats
        this.importInfo = {
          unit: stats.unit, unitDetected: stats.unitDetected, readValues: stats.readValues,
          ignoredLines: stats.ignoredLines, sourceIntervalMinutes: stats.sourceIntervalMinutes,
          sourceIntervalsMinutes: stats.sourceIntervalsMinutes || [],
          prm: stats.prm, nbCompteurs: stats.nbCompteurs || 0,
        }
        this.showUnitChange = false
        this.csvUploadStatus = 'saved'
        this.isDirty = false
        this.calculateEstimation()
        // nouvelle courbe : étude à recalculer, enregistrée aussitôt (PALLUEL, 14/10/2026 : réimport puis départ
        // sans « Étape suivante », l'étude pointait sur l'ancienne courbe, supprimée)
        this.SET_DIRTY(true)
        this.persistForm()
        this.$emit('save-draft')
        const firstMonthIdx = stats.monthly.findIndex(m => m.kwh > 0)
        if (firstMonthIdx >= 0) this.selectedMonth = firstMonthIdx
      } catch (e) {
        if (this.ecranQuitte) return
        const data = e && e.response ? e.response.data : null
        const msg = data && data.error && data.error.message ? data.error.message : (data && data.message) || ''
        const compteurs = e && e.response && e.response.status === 422 && data && data.data && data.data.compteurs
        if (compteurs) {
          // plusieurs compteurs dans le fichier : on demande lequel, l'import reprend dès qu'il est choisi
          this.csvErrorMessage = msg || 'Ce fichier contient plusieurs compteurs. Choisissez celui de l\'étude.'
          this.compteursFichier = compteurs
          this.compteursCoches = []
          this.prmCsv = null
          this.csvUploadStatus = 'compteur'
        } else if (e && e.response && e.response.status === 422) {
          // unité absente du fichier : on la demande, l'import reprend dès qu'elle est choisie
          this.csvErrorMessage = msg || "L'unité des valeurs n'est pas indiquée dans le fichier. Choisissez-la."
          const proposition = data && data.data ? data.data : null
          this.uniteProposee = proposition && proposition.suggestedUnit
            ? { unit: proposition.suggestedUnit, reason: proposition.suggestedUnitReason || '' }
            : null
          // présélectionnée : l'utilisateur n'a plus qu'à valider (ou à choisir une autre unité)
          this.form.uniteCsv = this.uniteProposee ? this.uniteProposee.unit : null
          this.csvUploadStatus = 'unit'
        } else {
          this.csvErrorMessage = msg && msg !== 'Internal server error' ? msg : 'Erreur — vérifiez le format du fichier'
          this.csvUploadStatus = 'error'
        }
      }
    },
    async loadPredefinedStats() {
      this.predefinedStatsStatus = 'loading'
      this.curveStats = null
      try {
        const token = authenticationService.status.jwtToken
        // tarif unique : pas d'heures creuses, mais le bilan complet est relu sur le serveur (B01)
        const periodesHC = this.form.typeTarif === 'unique'
          ? []
          : (this.form.periodesHC && this.form.periodesHC.length ? this.form.periodesHC : [{ debut: '22:00', fin: '06:00' }])
        const response = await axios.post(
          conf.API_URL + '/wizard/predefined-curve/' + this.form.profilPredifini + '/stats',
          { periodesHC: JSON.stringify(periodesHC), plages: this.plagesJson() },
          { headers: { Authorization: 'Bearer ' + token } }
        )
        if (this.ecranQuitte) return
        const stats = response.data.data
        this.SET_CONSUMPTION_CURVE_ID({ id: stats.curveId, status: 'saved', nomFichier: '' })
        this.oublierConsentement()
        this.curveStats = stats
        this.predefinedStatsStatus = 'ok'
        this.isDirty = false
        this.calculateEstimation()
      } catch (e) {
        this.predefinedStatsStatus = 'error'
      }
    },
    async exportCsv() {
      if (!this.consumptionCurveId) return
      this.csvExportLoading = true
      try {
        const token = authenticationService.status.jwtToken
        const response = await axios.get(
          conf.API_URL + '/wizard/curve/' + this.consumptionCurveId + '/export',
          { headers: { Authorization: 'Bearer ' + token }, responseType: 'blob' }
        )
        const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
        const a = document.createElement('a')
        a.href = url
        a.download = 'courbe_de_charge_' + this.consumptionCurveId + '.csv'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) { /* silencieux */ } finally {
        this.csvExportLoading = false
      }
    },
    formatKwh(v) { return v ? Math.round(v).toLocaleString('fr-FR') : '—' },
    formatKw(v) { return v ? v.toFixed(2) : '—' },
    formatEur(v) { return v ? Math.round(v).toLocaleString('fr-FR') : '—' },
    formatNumber(n) {
      return Number(n).toLocaleString('fr-FR').replace(/\u202f/g, ' ')
    },
    formatInterval(min) {
      if (!min) return '—'
      return min >= 60 ? `${min / 60}h` : `${min} min`
    },
    addPeriode(cle = 'periodesHC') {
      this.form[cle].push({ debut: '00:00', fin: '00:00' })
    },
    removePeriode(idx, cle = 'periodesHC') {
      this.form[cle].splice(idx, 1)
      this.markDirty() // un clic n'est pas une saisie : le bilan doit être recalculé (B39)
    },
    startPolling() {
      this.stopPolling()
      // suivi toutes les secondes (09/10/2026, 3 s auparavant) ; un suivi encore en cours n'est pas doublé
      this.pdlPollTimer = setInterval(() => { this.pollStatus() }, 1000)
    },
    stopPolling() {
      if (this.pdlPollTimer) {
        clearInterval(this.pdlPollTimer)
        this.pdlPollTimer = null
      }
    },
    async pollStatus() {
      if (!this.pdlConsentCode || this.pdlPollEnCours) return
      this.pdlPollEnCours = true
      try {
        const token = authenticationService.status.jwtToken
        const response = await axios.get(
          conf.API_URL + '/wizard/enedis-status',
          {
            params: { code: this.pdlConsentCode },
            headers: { Authorization: 'Bearer ' + token },
          }
        )
        if (this.ecranQuitte) return
        const status = response.data.data
        if (status.status === 'WAITING_CONSENT') {
          this.pdlStatus = 'waiting'
        } else if (status.status === 'FETCHING') {
          this.pdlStatus = 'fetching'
          this.pdlProgress = status.progress || 0
        } else if (status.status === 'DONE') {
          this.stopPolling()
          this.pdlStatus = 'done'
          this.pdlProgress = 100
          this.lireAvertissementsEnedis(status)
          // copie de la courbe pour cette étude : celle du consentement peut servir à d'autres études (lot 14)
          const curveId = await this.copierCourbeConsentement(this.pdlConsentCode, status.curveId)
          if (this.ecranQuitte) return
          this.SET_CONSUMPTION_CURVE_ID({ id: curveId, status: 'saved', nomFichier: '' })
          await this.recomputeStats()
          if (this.ecranQuitte) return
          // L'import Enedis dure plusieurs minutes : persister immédiatement le brouillon
          // pour que la courbe ne soit pas perdue si l'utilisateur recharge la page ou
          // quitte l'étape sans cliquer sur « Étape suivante ».
          this.persistForm()
          this.$emit('save-draft')
        } else if (status.status === 'ERROR') {
          this.stopPolling()
          this.pdlStatus = 'error'
          this.pdlErrorMessage = status.errorMessage || 'Erreur lors de la récupération des données Enedis.'
          this.pdlPeutReessayer = !!status.peutReessayer
        }
      } catch (e) { /* silencieux — on réessaie au prochain tick */ } finally {
        this.pdlPollEnCours = false
      }
    },
    async copierCourbeConsentement(code, repli) {
      try {
        const token = authenticationService.status.jwtToken
        const r = await axios.post(conf.API_URL + '/wizard/enedis-consentement/courbe', null, {
          params: { code }, headers: { Authorization: 'Bearer ' + token },
        })
        if (r.data && r.data.statusCode === 200 && r.data.data) return r.data.data
      } catch (e) { /* repli : courbe du consentement */ }
      return repli
    },
    // étude rouverte sur un consentement : sa copie de la courbe est-elle la version actuelle du consentement ?
    async verifierVersionCourbe() {
      if (!this.enedisConsentCode || !this.consumptionCurveId) return
      try {
        const token = authenticationService.status.jwtToken
        const r = await axios.get(conf.API_URL + '/wizard/enedis-consentement/courbe-a-jour', {
          params: { code: this.enedisConsentCode, curveId: this.consumptionCurveId },
          headers: { Authorization: 'Bearer ' + token },
        })
        const v = r.data && r.data.data
        this.versionPlusRecente = v && v.aJour === false ? { date: v.dateVersion || null } : null
      } catch (e) { this.versionPlusRecente = null /* vérification facultative */ }
    },
    async utiliserVersionPlusRecente() {
      if (!this.consentementsRecus.length) await this.chargerConsentementsRecus()
      const c = this.consentementsRecus.find(x => x.code === this.enedisConsentCode)
      if (!c) { this.pdlErrorMessage = 'Ce consentement n\'est plus disponible (expiré ?) : la courbe ne peut pas être mise à jour.'; this.pdlStatus = 'error'; return }
      await this.utiliserConsentement(c, this.pdlNumber || this.form.pdlNumber)
    },
    // un fichier ou un profil type remplace la courbe : le consentement n'est plus celui de l'étude (20/10/2026)
    oublierConsentement() {
      this.stopPolling()
      this.versionPlusRecente = null
      this.choixConsentement = ''
      this.pdlConsentCode = ''
      this.pdlStatus = 'idle'
      this.lireAvertissementsEnedis({})
      this.SET_STEP3({ enedisConsentCode: '', pdlNumber: '' })
      this.form.pdlNumber = ''
    },
    async chargerConsentementsRecus() {
      try {
        const token = authenticationService.status.jwtToken
        const r = await axios.get(conf.API_URL + '/wizard/enedis-consentements', { headers: { Authorization: 'Bearer ' + token } })
        this.consentementsRecus = (r.data && r.data.data) || []
      } catch (e) {
        this.consentementsRecus = []
      }
    },
    libelleCourbe(c) {
      if (c.courbe === 'PRETE') return 'Courbe reçue'
      if (c.courbe === 'EN_COURS') return 'Téléchargement en cours' + (c.avancement ? ' (' + c.avancement + ' %)' : '')
      if (c.courbe === 'ERREUR') return 'Courbe non reçue : nouvel essai au choix'
      return 'À télécharger'
    },
    dateFr(d) {
      if (!d) return ''
      const [a, m, j] = String(d).substring(0, 10).split('-')
      return j + '/' + m + '/' + a
    },
    // un consentement reçu : courbe prête → utilisée aussitôt ; sinon le téléchargement est lancé ou suivi
    /**
     * « Nouvelle demande de consentement » (12/10/2026) : enregistrée aussitôt avec le client de la Step 1 (e-mail,
     * portail particulier : le seul où Enedis propose Data Connect), pour 3 mois, puis lien et e-mail au client dans une fenêtre.
     */
    async nouvelleDemande() {
      this.demandeErreur = ''
      this.demandeEnCours = true
      try {
        const token = authenticationService.status.jwtToken
        const r = await axios.post(conf.API_URL + '/enedis-consent/create', {
          description: this.descriptionDemande,
          durationMonth: 3,
          email: this.clientEmail || '',
        }, { headers: { Authorization: 'Bearer ' + token } })
        const cree = r && r.data && r.data.data
        if (!cree || !cree.code) throw new Error('réponse sans code')
        this.lienCopie = false
        this.messageCopie = false
        this.demandeCreee = cree
      } catch (e) {
        this.demandeErreur = 'La demande de consentement n\'a pas pu être créée.'
      } finally {
        this.demandeEnCours = false
      }
    },
    // message mis en forme avec un lien cliquable, à coller dans l'e-mail (13/10/2026)
    async copierMessageDemande() {
      try {
        await copierMessageConsentement(this.demandeLien, authenticationService.getAgence())
        this.messageCopie = true
      } catch (e) {
        this.demandeErreur = 'Le message n\'a pas pu être copié : copiez le lien ci-dessus.'
      }
    },
    async copierLienDemande() {
      try {
        await navigator.clipboard.writeText(this.demandeLien)
        this.lienCopie = true
      } catch (e) {
        const champ = document.getElementById('demande-lien')
        if (champ) champ.select()
      }
    },
    ouvrirListeConsentements() {
      if (this.listeConsentementsOuverte) return
      this.rechercheConsentement = ''
      this.consentementSurvole = 0
      this.listeConsentementsOuverte = true
    },
    fermerListeConsentements() {
      this.listeConsentementsOuverte = false
      this.rechercheConsentement = ''
    },
    fermerListeConsentementsBientot() {
      setTimeout(() => this.fermerListeConsentements(), 150)
    },
    survolerConsentement(pas) {
      if (!this.listeConsentementsOuverte) { this.ouvrirListeConsentements(); return }
      const n = this.optionsConsentement.length
      if (n) this.consentementSurvole = (this.consentementSurvole + pas + n) % n
    },
    validerConsentementSurvole() {
      const o = this.optionsConsentement[this.consentementSurvole]
      if (this.listeConsentementsOuverte && o) this.selectionnerConsentement(o)
    },
    selectionnerConsentement(o) {
      this.fermerListeConsentements()
      if (this.$refs.comboConsentement) this.$refs.comboConsentement.blur()
      // le consentement déjà choisi : un nouveau clic recharge sa courbe (20/10/2026 ; avant, il ne se passait rien)
      if (o.cle === this.choixConsentement && o.consentement) { this.utiliserConsentement(o.consentement, o.prm); return }
      if (o.cle === this.choixConsentement) return
      this.choixConsentement = o.cle
      this.choisirConsentement()
    },
    // sélection dans la liste : la courbe est chargée aussitôt, sans bouton de validation (12/10/2026)
    choisirConsentement() {
      const o = this.optionsConsentement.find(x => x.cle === this.choixConsentement)
      if (!o) { this.cancelPdl(); return }
      if (!o.consentement) return // consentement déjà utilisé par l'étude, toujours en place
      this.utiliserConsentement(o.consentement, o.prm)
    },
    // courbe prête pour ce compteur → utilisée aussitôt ; sinon le téléchargement est lancé ou suivi
    async utiliserConsentement(c, prm) {
      prm = prm || c.prms[0]
      this.versionPlusRecente = null
      this.stopPolling()
      this.lireAvertissementsEnedis({})
      this.form.pdlNumber = prm
      this.pdlConsentCode = c.code
      this.pdlErrorMessage = ''
      this.SET_STEP3({ enedisConsentCode: c.code, pdlNumber: prm })
      this.SET_CONSUMPTION_CURVE_ID(null)
      this.curveStats = null
      this.pdlStatus = 'fetching'
      const memeCompteur = c.prmCourbe === prm
      this.pdlProgress = memeCompteur ? (c.avancement || 0) : 0
      // courbe non reçue (accès Enedis pas encore ouvert…) : le choix du consentement relance la récupération (16/10/2026)
      if (!memeCompteur || c.courbe === 'AUCUNE' || c.courbe === 'ERREUR') {
        try {
          const token = authenticationService.status.jwtToken
          await axios.post(conf.API_URL + '/wizard/enedis-consentement/telecharger', null, {
            params: { code: c.code, prm }, headers: { Authorization: 'Bearer ' + token },
          })
        } catch (e) {
          this.pdlStatus = 'error'
          const d = e && e.response && e.response.data
          this.pdlErrorMessage = (d && d.error && d.error.message) || (d && d.message) || 'Le téléchargement Enedis n\'a pas pu être lancé.'
          return
        }
      }
      if (this.ecranQuitte) return
      await this.pollStatus()
      if (this.pdlStatus === 'fetching' && !this.ecranQuitte) this.startPolling()
    },
    lireAvertissementsEnedis(status) {
      this.pdlAvertissementTemporaire = status.avertissementTemporaire || ''
      this.pdlAvertissementSansDonnees = status.avertissementSansDonnees || ''
      this.pdlPeutReessayer = !!status.peutReessayer
    },
    // étude rouverte : relire les avertissements de l'import Enedis déjà terminé
    async chargerAvertissementsEnedis() {
      if (!this.pdlConsentCode) return
      try {
        const token = authenticationService.status.jwtToken
        const response = await axios.get(conf.API_URL + '/wizard/enedis-status', {
          params: { code: this.pdlConsentCode },
          headers: { Authorization: 'Bearer ' + token },
        })
        const status = response.data.data
        if (status && status.status === 'DONE') this.lireAvertissementsEnedis(status)
      } catch (e) { /* avertissements facultatifs */ }
    },
    // ne retélécharge que les semaines refusées pour une raison passagère, puis réassemble la courbe
    async reessayerSemainesManquantes() {
      if (!this.pdlConsentCode) return
      this.pdlRetryLoading = true
      try {
        const token = authenticationService.status.jwtToken
        await axios.post(conf.API_URL + '/wizard/enedis-retry', null, {
          params: { code: this.pdlConsentCode },
          headers: { Authorization: 'Bearer ' + token },
        })
        this.lireAvertissementsEnedis({})
        this.pdlErrorMessage = ''
        this.pdlStatus = 'fetching'
        this.pdlProgress = 0
        this.startPolling()
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message
        this.pdlErrorMessage = msg || 'Le nouvel essai n\'a pas pu être lancé.'
        this.pdlStatus = 'error'
      } finally {
        this.pdlRetryLoading = false
      }
    },
    cancelPdl() {
      this.stopPolling()
      this.pdlStatus = 'idle'
      this.pdlConsentCode = ''
      this.pdlProgress = 0
      this.pdlErrorMessage = ''
      this.lireAvertissementsEnedis({})
      this.SET_STEP3({ enedisConsentCode: '' })
      this.SET_CONSUMPTION_CURVE_ID(null)
      this.curveStats = null
    },
    persistForm() {
      this.SET_STEP3({
        typeTarif: this.form.typeTarif,
        puissanceAbonnement: this.form.puissanceAbonnement,
        raccordement: this.form.raccordement,
        profileSource: this.form.profileSource,
        profilPredifini: this.form.profilPredifini,
        csvFile: this.form.csvFile,
        tarifAbonnementAnnuel: this.form.tarifAbonnementAnnuel,
        tarifHP: this.form.tarifHP,
        tarifHC: this.form.tarifHC,
        tarifHPHiver: this.form.tarifHPHiver,
        tarifHCHiver: this.form.tarifHCHiver,
        tarifHPEte: this.form.tarifHPEte,
        tarifHCEte: this.form.tarifHCEte,
        taxeelec: this.form.taxeelec,
        periodesHC: JSON.parse(JSON.stringify(this.form.periodesHC)),
        ...this.champsHsc(),
        pdlNumber: this.form.pdlNumber,
        consommationAnnuelleKwh: this.curveStats ? this.curveStats.annualKwh : 0,
        estimationFactureAnnuelle: this.estimationFacture,
      })
    },
    submit() {
      if (!this.isValid) return
      this.SET_STEP3({
        typeTarif: this.form.typeTarif,
        puissanceAbonnement: this.form.puissanceAbonnement,
        raccordement: this.form.raccordement,
        profileSource: this.form.profileSource,
        profilPredifini: this.form.profilPredifini,
        csvFile: this.form.csvFile,
        tarifAbonnementAnnuel: this.form.tarifAbonnementAnnuel,
        tarifHP: this.form.tarifHP,
        tarifHC: this.form.tarifHC,
        tarifHPHiver: this.form.tarifHPHiver,
        tarifHCHiver: this.form.tarifHCHiver,
        tarifHPEte: this.form.tarifHPEte,
        tarifHCEte: this.form.tarifHCEte,
        taxeelec: this.form.taxeelec,
        periodesHC: JSON.parse(JSON.stringify(this.form.periodesHC)),
        ...this.champsHsc(),
        pdlNumber: this.form.pdlNumber,
        consommationAnnuelleKwh: this.curveStats ? this.curveStats.annualKwh : 0,
        estimationFactureAnnuelle: this.estimationFacture,
      })
      this.$emit('next')
    },
  },
}
</script>

<style scoped>
.step-page {
  font-family: 'Raleway', sans-serif;
  background: #f5f5f5;
  min-height: calc(100vh - 140px);
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.step-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px; }

.step-badge {
  display: inline-block;
  background: #f5c518;
  color: #222;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.two-col {
  display: grid;
  /* minmax(0, …) : un contenu long (nom de fichier) ne fait plus grandir sa colonne (23/10/2026) */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.card-block {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-block__title {
  font-size: 15px;
  font-weight: 700;
  color: #222;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
  display: inline-block;
}

.section-separator {
  font-size: 12px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 16px 0 10px 0;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.info-hint {
  font-size: 12px;
  color: #aaa;
  margin-bottom: 10px;
}

.form-group { margin-bottom: 14px; }

.form-row { display: flex; gap: 12px; }

.form-group--half { flex: 1; min-width: 0; }
.form-group--full { flex: 1; }

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fafafa;
  transition: border-color 0.15s;
}

.checkbox-label:hover { border-color: #f5c518; }

.checkbox-input {
  width: 16px;
  height: 16px;
  accent-color: #f5c518;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-text {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.tarif-type-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.tarif-type-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.tarif-type-option--active {
  border-color: #f5c518;
  background: #fffdf0;
  color: #222;
}

.tarif-type-option input[type="radio"] {
  accent-color: #f5c518;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.tarif-saison-group {
  margin-bottom: 8px;
}

.tarif-saison-label {
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  padding-left: 2px;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.form-input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  color: #222;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-input:focus { border-color: #f5c518; box-shadow: 0 0 0 2px rgba(245,197,24,0.2); }

.form-select {
  appearance: none;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 12px center #fff;
  cursor: pointer;
}

/* Radio options */
.radio-option {
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  transition: border-color 0.15s, background 0.15s;
}

.radio-option.active {
  border-color: #f5c518;
  background: #fffdf0;
}

.radio-option--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.radio-option--disabled * {
  pointer-events: none;
}

.radio-label {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.radio-label input[type="radio"] {
  accent-color: #f5c518;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.radio-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 24px;
}

.radio-content {
  margin-top: 12px;
  padding-left: 24px;
}

.file-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-browse {
  padding: 7px 16px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  white-space: nowrap;
}

.btn-browse:hover { border-color: #999; }

.file-name {
  font-size: 13px;
  color: #666;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* nom du fichier importé tronqué s'il est trop long (texte complet au survol) */
.csv-status.courbe-reprise {
  display: flex;
  max-width: 100%;
  gap: 4px;
  min-width: 0;
}
.courbe-reprise__libelle {
  flex-shrink: 0;
  white-space: nowrap;
}
.courbe-reprise__nom {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.csv-hints {
  font-size: 11px;
  color: #aaa;
  line-height: 1.5;
  margin-top: 6px;
}






.csv-status {
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-block;
}
.csv-status--loading { background: #fff8e1; color: #f9a825; }
.csv-status--ok { background: #e8f5e9; color: #388e3c; }
.csv-status--err { background: #fce4ec; color: #c62828; }

.unit-request {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #f9a825;
  background: #fff8e1;
  border-radius: 8px;
  max-width: 460px;
}
.unit-request__msg { font-size: 13px; color: #6d4c00; margin: 0 0 8px; }
.unit-request__suggest { font-size: 13px; color: #333; margin: 0 0 8px; }
.unit-request__row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.unit-request__row .form-select { flex: 1; min-width: 200px; }
.btn-unit-validate {
  padding: 6px 14px; border: none; border-radius: 6px; background: #388e3c; color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.form-select--attention { border: 2px solid #c62828; }
.choix-compteur { max-width: 620px; }
.compteur-ligne { display: flex; gap: 8px; align-items: center; margin-top: 6px; }
.compteur-coche { width: 18px; height: 18px; flex-shrink: 0; accent-color: #388e3c; cursor: pointer; }
.btn-somme-compteurs { margin-top: 10px; }
.compteur-option {
  display: block; width: 100%; text-align: left; margin-top: 0; padding: 8px 10px; border: 1px solid #ddd;
  border-radius: 6px; background: #fff; font-size: 13px; cursor: pointer;
}
.compteur-option:hover { border-color: #f5c518; background: #fffbea; }
.alerte-tarifs {
  margin: 4px 0 12px; padding: 10px 12px; border: 1px solid #c62828; background: #fce4ec; border-radius: 8px;
  display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
}
.alerte-tarifs__msg { font-size: 13px; color: #8e0000; margin: 0; flex: 1; min-width: 240px; }
.import-info { font-size: 13px; color: #444; margin-top: 8px; line-height: 1.6; }
.import-info__change { margin-left: 6px; font-size: 12px; }
.import-info__warn { color: #c62828; }
.import-info__unit { margin-top: 6px; max-width: 360px; }

.import-action-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.btn-import {
  padding: 9px 24px;
  background: #f5c518;
  color: #222;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.btn-import:hover:not(:disabled) { background: #e6b800; }

.btn-import--loading {
  background: #f0f0f0;
  color: #999;
  cursor: not-allowed;
}

.btn-add-period {
  background: none;
  border: 1px dashed #f5c518;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-family: 'Raleway', sans-serif;
  color: #f5a518;
  cursor: pointer;
  margin-top: 6px;
}

.btn-add-period:hover { background: #fff8e1; }

.btn-remove-period {
  background: none;
  border: none;
  color: #e53935;
  font-size: 14px;
  cursor: pointer;
  align-self: flex-end;
  padding: 0 4px;
  line-height: 38px;
}

.periode-row { align-items: flex-end; }

/* Estimation facture */
.form-hint {
  font-size: 12px;
  color: #6b7280; /* plus lisible (13/10/2026) */
  margin-top: 4px;
}


/* ---- Consentements reçus (lot 14) ---- */
.consent-combo { position: relative; flex: 1; min-width: 0; }
.consent-combo .consent-select { width: 100%; }
.consent-liste { position: absolute; top: calc(100% + 2px); left: 0; right: 0; z-index: 40; max-height: 280px; overflow-y: auto;
  margin: 0; padding: 4px 0; list-style: none; background: #fff; border: 1px solid #dde3ea; border-radius: 8px; box-shadow: 0 6px 18px rgba(0,0,0,.12); }
.consent-option { padding: 8px 12px; font-size: .88rem; cursor: pointer; }
.consent-option--survol { background: #eef4fb; }
.consent-option--choisie { font-weight: 700; }
.consent-option--vide { color: #888; cursor: default; }
.consent-choix { display: flex; gap: 8px; align-items: center; }
.consent-select { flex: 1; min-width: 0; }
.btn-consent-outil { flex: none; width: 38px; height: 38px; font-size: 1.1rem; background: #fff; color: #1565c0; border: 1px solid #c5d3e2; border-radius: 6px; cursor: pointer; }
.btn-consent-outil:hover:not(:disabled) { background: #eef4fb; }
.demande-modale { position: fixed; inset: 0; background: rgba(20, 24, 40, .45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
.demande-modale__boite { background: #fff; border-radius: 10px; padding: 22px 24px; width: 100%; max-width: 560px; box-shadow: 0 10px 30px rgba(0,0,0,.2); }
.demande-modale__titre { font-weight: 700; font-size: 1.05rem; margin-bottom: 6px; }
.demande-modale__texte { font-size: .88rem; color: #555; margin: 0 0 14px; }
.demande-modale__actions { display: flex; gap: 10px; align-items: center; justify-content: flex-end; margin-top: 16px; flex-wrap: wrap; }
.demande-modale__mail { text-decoration: none; display: inline-block; }
.btn-nouvelle-demande { cursor: pointer; background: #fff; display: inline-block; margin-top: 10px; padding: 6px 12px; font-size: .85rem; font-weight: 600; color: #1565c0; border: 1px solid #1565c0; border-radius: 6px; text-decoration: none; }
.btn-nouvelle-demande:hover { background: #eef4fb; text-decoration: none; }
/* ---- Bilan courbe de charge ---- */
.bilan-panel {
  background: #fff;
  border-radius: 16px;
  padding: 28px 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.bilan-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #29b6f6;
}

.bilan-panel__title {
  font-size: 17px;
  font-weight: 800;
  color: #1a1a2e;
}

.btn-export-csv {
  padding: 7px 18px;
  border: 1px solid #29b6f6;
  border-radius: 20px;
  background: #fff;
  color: #0288d1;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.btn-export-csv:hover:not(:disabled) {
  background: #e1f5fe;
}

.btn-export-csv:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recalcul-row {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}

.btn-recalcul-form {
  width: 100%;
  padding: 10px;
  background: #f5c518;
  color: #222;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-recalcul-form:hover:not(:disabled) { background: #e6b800; }
.btn-recalcul-form:disabled { background: #f0f0f0; color: #999; cursor: not-allowed; }

.bilan-alerte {
  margin: 12px 0;
  padding: 10px 14px;
  border-left: 4px solid #f5a623;
  background: #fff8e8;
  color: #6b4a00;
  font-size: 13px;
  border-radius: 4px;
}

.bilan-alerte--forte {
  border-left-width: 6px;
  border-left-color: #e65100;
  background: #fff0e0;
  color: #7a2e00;
  font-size: 14px;
  font-weight: 600;
}

.bilan-qualite {
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  margin: 12px 0 0; padding: 10px 16px; border-radius: 8px; border: 1px solid; border-left-width: 6px;
}
.bilan-qualite__label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.bilan-qualite__value { font-size: 20px; font-weight: 800; }
.bilan-qualite__detail { font-size: 13px; }
.bilan-qualite--vert { background: #e8f5e9; border-color: #2e7d32; color: #1b5e20; }
.bilan-qualite--orange { background: #fff3e0; border-color: #ef6c00; color: #a84300; }
.bilan-qualite--rouge { background: #ffebee; border-color: #c62828; color: #b71c1c; }
.bilan-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
  margin-bottom: 24px;
}

.bilan-kpi {
  background: #f8fbff;
  border: 1px solid #e3f2fd;
  border-radius: 10px;
  padding: 14px 16px;
  text-align: center;
}

.bilan-kpi--highlight {
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d54 100%);
  border-color: #1a1a2e;
  color: #fff;
}

.bilan-kpi--highlight .bilan-kpi__label { color: rgba(255,255,255,0.7); }
.bilan-kpi--highlight .bilan-kpi__value { color: #f5c518; }
.bilan-kpi--highlight .bilan-kpi__unit { color: rgba(255,255,255,0.6); }

.bilan-kpi__label {
  font-size: 11px;
  font-weight: 600;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}

.bilan-kpi__value {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;
}

.bilan-kpi__unit {
  font-size: 11px;
  color: #90a4ae;
  margin-top: 4px;
}

.bilan-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.bilan-tab {
  padding: 6px 20px;
  border-radius: 20px;
  border: 1px solid #ddd;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  color: #888;
  transition: all 0.15s;
}

.bilan-tab--active {
  background: #29b6f6;
  border-color: #29b6f6;
  color: #fff;
}

.bilan-select {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  color: #444;
  outline: none;
  cursor: pointer;
}

.bilan-chart-wrapper {
  width: 100%;
  overflow: hidden;
}

.bilan-svg {
  width: 100%;
  height: auto;
  display: block;
}

.bilan-chart-unit {
  text-align: right;
  font-size: 11px;
  color: #aaa;
  margin-top: 4px;
}

/* Footer */
.step-footer { display: flex; gap: 12px; padding-top: 8px; }

.btn-back {
  padding: 11px 28px;
  border: 1px solid #ccc;
  border-radius: 24px;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
}

.btn-back:hover { border-color: #999; }

.btn-next {
  padding: 11px 32px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
}

.btn-next:hover:not(:disabled) { background: #e6b800; }
.btn-next:disabled { opacity: 0.4; cursor: not-allowed; }

/* PDL Enedis */
.btn-enedis {
  padding: 10px 20px;
  border: none;
  border-radius: 24px;
  background: #1565c0;
  color: #fff;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-enedis:hover:not(:disabled) { background: #0d47a1; }
.btn-enedis:disabled { opacity: 0.45; cursor: not-allowed; }

.pdl-status-block {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 10px;
}
.pdl-status-block--done { background: #e8f5e9; }
.pdl-warning { margin-top: 8px; padding: 8px 12px; border-radius: 8px; background: #fff8e1; border: 1px solid #f9a825; }
.pdl-warning--temporaire { background: #fff3e0; border-color: #fb8c00; }
.pdl-warning__text { font-size: 13px; color: #6d4c00; margin: 0; }
.btn-enedis-retry {
  margin-top: 8px; padding: 6px 14px; border: none; border-radius: 6px; background: #fb8c00; color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.btn-enedis-retry:disabled { opacity: 0.6; cursor: default; }

.pdl-status-icon {
  font-size: 22px;
  flex-shrink: 0;
}
.pdl-status-icon--waiting { color: #fb8c00; }
.pdl-status-icon--fetching { color: #1565c0; animation: spin 1.2s linear infinite; display: inline-block; }
.pdl-status-icon--done { color: #2e7d32; }

@keyframes spin { 100% { transform: rotate(360deg); } }

.pdl-status-content { flex: 1; min-width: 0; }
.pdl-status-label { font-size: 13px; font-weight: 700; color: #333; }
.pdl-status-hint { font-size: 11px; color: #888; margin-top: 2px; }

.pdl-progress-bar {
  background: #dde;
  border-radius: 4px;
  height: 6px;
  margin: 6px 0 4px 0;
  overflow: hidden;
}
.pdl-progress-fill {
  height: 100%;
  background: #1565c0;
  border-radius: 4px;
  transition: width 0.4s;
}

.btn-cancel-small {
  padding: 5px 12px;
  border: 1px solid #ccc;
  border-radius: 16px;
  background: #fff;
  color: #666;
  font-size: 12px;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  white-space: nowrap;
}
.btn-cancel-small:hover { border-color: #999; }

.form-hint--warn { color: #e65100; }
.mode-operatoire { display: table; margin: -6px 0 14px; font-size: 13px; font-weight: 600; color: #1c7c54; } /* sous le titre (bloc), pas à côté */
.mode-operatoire:hover { text-decoration: underline; }
.lien-modele { color: #1c7c54; font-weight: 600; text-decoration: underline; }
</style>
